import {
  AlertDialog,
  BodyText,
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Tooltip,
  toast,
} from "@/components/ui";
import { useEpisodesOfCareList } from "@/network/useEpisodesOfCare";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import type * as fhirTypes from "fhir/r4";
import { CloudUpload, Trash2 } from "lucide-react";
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useFormState } from "react-hook-form";
import { FileViewer } from "../components/FileViewer";
import { useFileCategories } from "../network/useFileCategories";
import { useUpdateFile } from "../network/useUpdateFile";
import { useVoidFile } from "../network/useVoidFile";
import { type EditFileForm, editFileFormSchema } from "../schemas";
import { formatUploadedString } from "../utils";

interface EditFileProps {
  onSave(): void;
  file: fhirTypes.DocumentReference;
}

export const EditFile = ({ onSave, file }: EditFileProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const [showVoidPrompt, setShowVoidPrompt] = useState(false);
  const { data: eocData } = useEpisodesOfCareList({ patientId });
  const { data: categoriesData } = useFileCategories();
  const { mutateAsync, isPending } = useUpdateFile(patientId, file.id);
  const voidFile = useVoidFile(patientId, file.id);

  const eoc = file.context?.encounter?.[0]?.identifier?.value;
  const category = file.type?.coding?.[0]?.code;
  const name = file.content[0].attachment.title;

  const defaultValues = useMemo(() => {
    return {
      category: category || "",
      eoc: eoc || "",
      file: null, // only exists when a user re-uploads a new file
      filename: name || "",
    };
  }, [category, eoc, name]);

  const form = useForm<EditFileForm>({
    resolver: zodResolver(editFileFormSchema),
    mode: "onSubmit",
    defaultValues,
  });

  const onSubmit = async (values: EditFileForm) => {
    try {
      await mutateAsync({
        categoryId: values.category,
        eocId: values.eoc,
        ...(values.file && { file: values.file }),
        ...(values.filename &&
          values.filename !== name && { file_name: values.filename }),
      });
      toast.success("File updated successfully");
      onSave();
    } catch (error) {
      toast.error("Failed to edit file. Please try again.");
    }
  };

  const handleClickReUpload = () => {
    if (fileInputRef.current) fileInputRef.current.value = ""; // clear existing file if choosing a new one
    fileInputRef.current?.click();
  };

  const handleVoidFile = async () => {
    try {
      await voidFile.mutateAsync();
      onSave();
    } catch (error) {
      toast.error("Failed to delete file. Please try again.");
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    form.setValue("file", file, { shouldDirty: true });
  };

  const { isDirty } = useFormState({ control: form.control });

  useEffect(() => {
    // reset form when navigating to a new file
    if (defaultValues) form.reset(defaultValues);
  }, [defaultValues, form.reset]);

  return (
    <>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex items-center gap-3 mt-1">
          <Controller
            control={form.control}
            name="filename"
            render={({ field, fieldState }) => (
              <Field className="flex-1">
                <Input
                  id="filename"
                  placeholder="Enter file name (optional)"
                  {...field}
                  data-testid="filename-input"
                  className="flex-1"
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          <div className="flex">
            <Tooltip tooltipContentText="Re-upload" side="bottom">
              <Button
                variant="icon"
                onClick={handleClickReUpload}
                className="py-1 px-2"
                data-testid="reupload-file-button"
              >
                <CloudUpload className="text-link w-4" />
              </Button>
            </Tooltip>
            <Tooltip tooltipContentText="Void" side="bottom">
              <Button
                variant="icon"
                data-testid="delete-file-button"
                onClick={() => setShowVoidPrompt(true)}
                className="py-1 px-2"
              >
                <Trash2 className="text-link w-4" />
              </Button>
            </Tooltip>
          </div>
        </div>
        <div
          className="flex flex-wrap items-end gap-3 mt-1"
          data-testid="file-edit-controls"
        >
          <Controller
            control={form.control}
            name="category"
            render={({ field }) => (
              <Field className="min-w-40 flex-1 basis-40">
                <FieldLabel htmlFor="category">Category</FieldLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="category" aria-label="select-category">
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {categoriesData?.categories.map(({ id, type }) => (
                        <SelectItem key={id} value={id}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="eoc"
            render={({ field }) => (
              <Field className="min-w-40 flex-1 basis-40">
                <FieldLabel htmlFor="eoc">EOC</FieldLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="eoc" aria-label="select-eoc">
                    <SelectValue placeholder="Select EOC" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {eocData?.map(({ id, title }) => (
                        <SelectItem key={id} value={id}>
                          {title}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            )}
          />

          <Button
            size="sm"
            type="submit"
            isLoading={isPending}
            disabled={!isDirty || isPending}
          >
            Save
          </Button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleFileChange}
          multiple
        />

        <div data-testid="file-preview">
          <Controller
            control={form.control}
            name="file"
            render={({ field }) => (
              <FileViewer existingFile={file} newFile={field.value} />
            )}
          />
        </div>
      </form>

      <Separator className="my-3" />

      <div className="flex items-center gap-1">
        <CloudUpload size={12} />
        <BodyText size="xs" textColor="tertiary">
          {formatUploadedString(
            file.authenticator?.display,
            file.content[0].attachment.creation || "",
          )}
        </BodyText>
      </div>
      <AlertDialog
        isOpen={showVoidPrompt}
        onOpenChange={setShowVoidPrompt}
        isControlled
        title="Delete File"
        body={`Are you sure you want to delete this file? This will remove it entirely from the patient's medical record, and should only be used if the file was added in error (i.e. the wrong patient).`}
        actionLabel="Yes"
        cancelLabel="No"
        onAction={() => {
          setShowVoidPrompt(false);
          handleVoidFile();
        }}
        onCancel={() => {
          setShowVoidPrompt(false);
        }}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </>
  );
};
